import { useCallback, useEffect, useRef, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { Loader } from '@googlemaps/js-api-loader';
import useSWR from 'swr';
import { Feature, getFeature, getId, MapFeature, useMapStore, useToastStore } from './store';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { animateMarker, createTemplate, fetchWithToken, getPin, getPolygonBoundsCenter, getStatusColor, isPointInViewport, outagesMapCenter, outagesMapSettings, renderCluster } from './mapUtils';
import MapLegend from './MapLegend';
import FilterMenu from './menus/FilterMenu';
import SlidePanel from './SlidePanel';
import MobileMenu from './menus/MobileMenu';
import SideMenu from './menus/SideMenu';
import { Spinner } from 'components/non_residential/atoms/Spinner';


type OutagesMapProps = {
  outagesFormUrl: string
  getEventsApi: string
  getEventsKey: string
  getOutagesApi: string
  getOutagesKey: string
  mapApiKey: string
  reportFormUrl: string;
}

const OutagesMap = ({ 
  outagesFormUrl,
  getEventsApi,
  getEventsKey,
  getOutagesApi,
  getOutagesKey,
  mapApiKey,
  reportFormUrl
}: OutagesMapProps) => {
  const [dimensions, setDimensions] = useState({
    width: 854.656,
    height: 600
  });
  const infoWindow = useRef<google.maps.InfoWindow>(null)
  const clusterer = useRef<MarkerClusterer>(null)

  // Zustand store hooks
  const { 
    showLoadingToast, 
    dismissToast, 
    updateToastWithRetry, 
    showErrorToast,
    mapDataError,
    eventDataError 
  } = useToastStore();

  const { 
    display, 
    features, 
    filterStatus, 
    isDesktop, 
    hasData,
    map, 
    sidePanelWidth,
    mapReady,
    mapLoadError,
    resetMapView, 
    setDisplay, 
    setFeatures, 
    setIsDesktop, 
    setSelectedFeature, 
    setVisibleFeatures, 
    setMap,
    setMapReady,
    setMapLoadError,
    setSearchPlace
  } = useMapStore();

  // call api for map outages data
  const { data: mapData, mutate } = useSWR(
    !mapLoadError && !hasData.mapData ? getOutagesApi : null,
    url => {
      // Show loading toast when fetch starts
      showLoadingToast('map');
      return fetchWithToken(url, getOutagesKey);
    },
    {
      errorRetryCount: 3,
      onSuccess: () => {
        // Clear loading toast on success
        dismissToast('map');
      },
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        if (error.status === 404) return;
        if (retryCount > 3) {
          showErrorToast('map', error, revalidate);
          return;
        }
        updateToastWithRetry('map', retryCount);
        setTimeout(() => revalidate({ retryCount }), 5000);
      }
    }
  );

  // call api for event data
  const { data: eventData, mutate: mutateEvents } = useSWR(
    !mapLoadError && !hasData.eventData ? getEventsApi : null,
    url => {
      // Show loading toast when fetch starts
      showLoadingToast('event');
      return fetchWithToken(url, getEventsKey);
    },
    {
      errorRetryCount: 3,
      onSuccess: () => {
        // Clear loading toast on success
        dismissToast('event');
      },
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        if (error.status === 404) return;
        if (retryCount > 3) {
          showErrorToast('event', error, revalidate);
          return;
        }
        updateToastWithRetry('event', retryCount);
        setTimeout(() => revalidate({ retryCount }), 5000);
      }
    },
    
  );

  const checkInfoWindowPosition = useCallback(() => {
    setVisibleFeatures()
    // Check if infoWindow exists and is open
    if (!infoWindow.current || !map) return;

    // Get the current viewport bounds
    const currentBounds = map.getBounds();

    // Get the InfoWindow position
    const infoWindowPosition = infoWindow.current.getPosition()

    // Get current map instance from the InfoWindow
    // InfoWindow is considered open if it's attached to a map
    const currentMap = infoWindow.current.isOpen
    const isInfoWindowOpen = currentMap !== null;
    if (!infoWindowPosition && isInfoWindowOpen) {
      infoWindow.current.close()
    }

    if (currentBounds && infoWindowPosition && isInfoWindowOpen) {
      if (!isPointInViewport(infoWindowPosition, currentBounds)) {
        infoWindow.current.close();
      }
    }
  }, [map])

  // Callback to initialize map
  const initMap = useCallback(async (mapContainer: HTMLDivElement) => {
    if (!mapContainer) return;
    try {
      const loader = new Loader({
        apiKey: mapApiKey,
        version: 'weekly',
        libraries: ['maps', 'places', 'core']
      });

      // Load the Maps JavaScript API
      const [mapsLibrary] = await Promise.all([
        loader.importLibrary('maps'),
        loader.importLibrary('marker'),
        loader.importLibrary('places'),
        loader.importLibrary('core')
      ]);

      const mapInstance = new mapsLibrary.Map(mapContainer, outagesMapSettings);
      mapInstance.addListener('idle', () => {
        if (!infoWindow.current) {
          infoWindow.current = new mapsLibrary.InfoWindow();
          setMapReady();
        }
      });
      setMap(mapInstance);
    } catch (error) {
      setMapLoadError()
      // console.error('Error initializing Google Maps:', (error as Error)?.message);
    }
  }, []);

  // Callback for monitoring map width changes
  const mapWidth = useCallback((widthContainer: HTMLDivElement) => {
    if (!widthContainer) return;
    let frameId: number;
    const observer = new ResizeObserver((entries) => {
      // Wrap the resize logic in requestAnimationFrame
      frameId = requestAnimationFrame(() => {
        for (let entry of entries) {
          const newWidth = entry.contentRect.width;
          setDimensions(d => ({
            ...d,
            width: newWidth >= 764 ? newWidth - 364 : newWidth
          }));
          setIsDesktop(newWidth);
        }
      });
    });
    observer.observe(widthContainer);
  }, []);

  // Callback for clearing existing elements
  const cleanup = useCallback(() => {
    features.forEach(feature => {
      feature.marker.map = null
      feature.polygon?.setMap(null)
    })
    infoWindow?.current?.close();
    map?.setZoom(5);
    map?.setCenter(outagesMapCenter);
    clusterer?.current?.clearMarkers()
    toast.dismiss()
  }, [dismissToast, infoWindow, map, features]);

  // handle closing slide out menu
  const handleCloseMenu = () => {
    // manage focus
    if (display === "filter") {
      document.getElementById("map_filter_button")?.focus()
    }
    if (display === "search") {
      document.getElementById("map_search_button")?.focus()
    }
    // clear search 
    setSearchPlace("")
    // close panel
    setTimeout(() => setDisplay(null))
  }

  // handle open infowwindow from pin click or accordion selection
  const handleOpenInfowindow = (id: number) => {
    setSelectedFeature(id);
    const feature = getFeature(id);
    if (map && feature && infoWindow.current) {
      setDisplay(null)
      // @ts-ignore google types are wrong, zoom exists
      if (map.zoom < 11) {
        map?.setZoom(dimensions.width > 800 ? 15 : 12)
      }
      feature.marker.position && map?.setCenter(feature.marker.position);
      let content = createTemplate(feature.data, outagesFormUrl);
      animateMarker(feature.marker, 'BOUNCE');
      setTimeout(() => {
        infoWindow.current?.setContent(content);
        infoWindow.current?.setPosition(feature.marker.position);
        infoWindow.current?.open({
          anchor: feature.marker,
          map,
        });
        setTimeout(() => { infoWindow.current?.focus() }, 500)
      });
    }
  }
 // middleware function for processing data into usable map features 
  const mapApiData = (mapFeature: MapFeature) => {
    // let id = getId();
    let id = mapFeature.properties.id
    let position: google.maps.LatLngLiteral | null = null;
    let polygon: google.maps.Polygon | null = null;

    // Get position and create polygon if needed
    if (mapFeature.geometry.type === 'Point') {
      const [lng, lat] = mapFeature.geometry.coordinates as number[];
      position = { lat, lng };
    } else if (mapFeature.geometry.type === 'Polygon') {
      const paths = (mapFeature.geometry.coordinates as number[][][])[0]
        .map(([lng, lat]) => ({ lat, lng }));

      polygon = new google.maps.Polygon({
        paths,
        strokeColor: getStatusColor(mapFeature.properties.status),
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: getStatusColor(mapFeature.properties.status),
        fillOpacity: 0.35,
      });

      position = getPolygonBoundsCenter(polygon);
    }
    // Create marker
    const parser = new DOMParser()
    const content = parser.parseFromString(getPin(mapFeature.properties.status), 'image/svg+xml').documentElement
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position,
      content,
      title: mapFeature.properties.name,
      gmpClickable: true
    })
    // open marker details on pin click
    marker.addListener('gmp-click', () => {
      handleOpenInfowindow(id)
    });
    return {
      id,
      data: {...mapFeature.properties, status: mapFeature.properties?.type?.includes("Major") ? "Major" : mapFeature.properties.status},
      marker,
      polygon: polygon!
    };
  }


  // Create features when api data is available
  useEffect(() => {
    if(!mapReady) return;
    if (mapData) {
      const newDataFeatures: Feature[] = mapData.map(mapApiData);
      setFeatures(newDataFeatures, 'mapData');
    }
    if (eventData) {
      const newEventFeatures: Feature[] = eventData.map(mapApiData);
      setFeatures(newEventFeatures, 'eventData');
    }
    setVisibleFeatures()
  }, [features, map, mapData, eventData, mapReady]);

  // Handle visibility based on filters
  useEffect(() => {
    if (!map || !mapReady) return;
    // Clear existing markers from clusterer
    clusterer.current?.clearMarkers()

    const visibleMarkers: google.maps.marker.AdvancedMarkerElement[] = [];

    features.forEach((feature) => {
      const isVisible = filterStatus[feature.data.status]

      // Handle marker visibility
      feature.marker.map = isVisible ? map : null;
      // Collect visible markers for clustering
      if (isVisible) {
        visibleMarkers.push(feature.marker);
      }

      // Handle polygon visibility if it exists
      if (feature.polygon) {
        feature.polygon.setMap(isVisible ? map : null);
      }
    });
    // Add visible markers to clusterer
    if (visibleMarkers.length > 0) {
      if (!clusterer.current) {
        clusterer.current = new MarkerClusterer({
          map,
          markers: visibleMarkers,
          algorithm: new SuperClusterAlgorithm({}),
          renderer: {
            render: ({ count, position }) => {
              const cluster = new google.maps.marker.AdvancedMarkerElement({
                position,
                content: renderCluster({ count, position })
              });

              cluster.addListener('click', () => {
                if (!map) return;

                const markers = clusterer.current?.getMap()?.get('markers') || [];
                const bounds = new google.maps.LatLngBounds();

                markers.forEach((marker: { position: google.maps.LatLng; }) => {
                  bounds.extend(marker.position as google.maps.LatLng);
                });

                map.fitBounds(bounds, 50);

                const zoom = map.getZoom();
                if (zoom && zoom > 15) {
                  map.setZoom(15);
                }
              });

              return cluster;
            }
          }
        });
      } else {
        clusterer.current.addMarkers(visibleMarkers)
      }

    }
    return () => {
      clusterer.current?.clearMarkers()
    }

  }, [map, mapReady, features, filterStatus]);

  useEffect(() => {
    if (!map) return;
    const boundsListener = map.addListener("bounds_changed", checkInfoWindowPosition);

    return () => {
      google.maps.event.removeListener(boundsListener);
    };
  }, [map, checkInfoWindowPosition]);

  useEffect(() => {
    if (!map) return;
    const zoomListener = map.addListener("zoom_changed", () => {
      const currentZoom = map.getZoom();
      if (currentZoom! < 10 && infoWindow.current?.isOpen) {
        infoWindow.current.close()
      }
    });

    return () => {
      google.maps.event.removeListener(zoomListener);
    };
  }, [map, checkInfoWindowPosition]);

  // Clean up toasts on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const hasError = (mapDataError && eventDataError) || mapLoadError;
  return (
    <div className='bg-ocean rounded-lg overflow-hidden border border-solid border-grey-600' ref={mapWidth}>
      {!isDesktop && <MobileMenu handleReset={resetMapView} />}
      
      <div className='flex relative overflow-hidden' id="map_stage" style={{ height: 600 }}>
        <div style={{ width: 365 }}>
          {isDesktop ? (
            <SideMenu 
              handleOpenInfowindow={handleOpenInfowindow} 
              isMapReady={mapReady} 
              resetMap={resetMapView}
              reportFormUrl={reportFormUrl}
            />
          ) : (
            <SlidePanel 
              title="Outages by suburb panel" 
              description='find outages grouped by suburbs or narrow the list using search and use them to navigate to the corresponding outage on the map' 
              isOpen={display === "search"} 
              onClose={handleCloseMenu} 
              isSidePanel={true} 
              width={sidePanelWidth}
            >
              <SideMenu 
                handleOpenInfowindow={handleOpenInfowindow} 
                isMapReady={mapReady} 
                resetMap={resetMapView}
                reportFormUrl={reportFormUrl} 
              />
            </SlidePanel>
          )}
        </div>

        <div className='relative bg-white'>
          <SlidePanel 
            title="Filter suburbs panel" 
            description="Choose filters to update the map" 
            isOpen={display === "filter"} 
            onClose={handleCloseMenu} 
            height={300}
          >
            <FilterMenu />
          </SlidePanel>
          
          <a 
            className='btn-cta text-wrap absolute bottom-1 left-2 w-4/5 sm:w-auto z-20'
            href={outagesFormUrl}
          >
            SIGN UP WATER SUPPLY ALERTS
          </a>
          
          <div className='w-full h-full relative overflow-hidden'>
            <ToastContainer
              role="alert"
              pauseOnHover
              pauseOnFocusLoss
              stacked
              style={{
                position: 'absolute',
                left: '5px',
                top: '5px',
                zIndex: 10
              }}
            />
            
            {hasError && (
              <div 
                className='absolute top-0 right-0 bottom-0 left-0 z-10 flex items-center justify-center bg-black/50 transition-opacity duration-500' 
                style={dimensions} 
                role="alert"
              >
                <div className='bg-validation-red shadow-md rounded-md p-8' style={{maxWidth: 420}}>
                  <h2 className="h3 text-white text-center">Something went wrong.</h2>
                  <p className="h6 text-white text-center">
                    <br/>
                    Please, {mapLoadError ? "reload this page tab and try again" : (<button 
                      onClick={() => {
                        mutate();
                        mutateEvents();
                      }} 
                      className='link text-white focus:outline-white' 
                      autoFocus
                    >
                      try again
                    </button>)} or contact us on 13 13 75.
                  </p>
                </div>
              </div>
            )}
            {
              !hasError && !mapReady && (
                <div className="absolute top-0 right-0 bottom-0 left-0 z-10 flex items-center justify-center bg-transparent">
                  <div>
                    <Spinner />
                    <p className="h4 mt-8">...loading map</p>
                  </div>
                </div>
              )
            }
            <div
              id="outages_map"
              className='w-full h-full flex items-center justify-center transition-opacity duration-500' 
              ref={initMap} 
              style={dimensions}
            ></div>
          </div>
        </div>
      </div>

      <MapLegend />
    </div>
  );
};

export default OutagesMap;

